import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import Popup from "../components/PopUP";
import api from "../Api";
import { useLocation } from "react-router-dom";
import GameBreadcrumb from "../components/GameBreadcrumb.js";
import MarkdownText from "../components/MarkdownText";

const getGameName = (gameType) => {
    if (gameType === "image-game") {
        return "Plane spotter";
    }
    else if (gameType === "encyclopaedia") {
        return "Citius. Altius. Longius.";
    }
    return "Jetting Around The World";
};

const DirectQuestionSetGameQuestions = () => {
    const [popupMessage, setPopupMessage] = useState("Loading page content...");
    const { pathname, hash: locationHash } = useLocation();
    const [loading, setLoading] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [gameInfo, setGameInfo] = useState({ game: "", level: "", difficulty: "" });
    const [editForm, setEditForm] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [setId, setSetId] = useState();
    const [isApproved, setIsApproved] = useState(false);
    const [hash, setHash] = useState();
    const [endPoint, setEndPoint] = useState();

    useEffect(() => {
        const fetchData = async () => {
            const parts = pathname.split("/").filter(Boolean);
            const idFromPath = parts[2];
            setSetId(idFromPath);

            const gameType = parts[1];
            let endpoint = null;

            if (gameType === "trivia") {
                endpoint = "/trivia/";
            }
            else if (gameType === "image-game") {
                endpoint = "/image-game/"
            }
            else if (gameType === "encyclopaedia") {
                endpoint = "/encyclopaedia/"
            }
            if (!endpoint) return;

            setEndPoint(endpoint);

            if (!idFromPath || idFromPath === "undefined") {
                setLoading(false);
                return;
            }

            const question_id = locationHash.substring(1);
            setHash(question_id);

            setTimeout(() => {
                const element = document.getElementById(question_id);
                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            }, 100);

            try {
                const response = await api.get(`${endpoint}admin/question/${idFromPath}`);
                console.log("API Response:", response.data);

                const data = response.data?.data ?? response.data;

                setQuestions(data.category_types || []);
                setGameInfo({
                    game: getGameName(gameType),
                    level: data.level || "",
                    difficulty: data.difficulty || "",
                });
                setIsApproved(data.is_approved ?? false);
            } catch (error) {
                console.error("API Error:", error.message);
                setPopupMessage("Failed to load questions");
            }

            setLoading(false);
        };

        fetchData();
    }, [pathname, locationHash]);

    const allQuestions = questions.flatMap((category) =>
        (category.questions || []).map((question) => ({
            ...question,
            category_type: category.type,
            category_name: category.name,
        }))
    );

    const uploadImageFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        const response = await api.post("/image-game/file-upload", formData);
        const uploadedUrl = response.data?.url;

        if (!uploadedUrl) {
            throw new Error("Image upload did not return a URL");
        }

        return uploadedUrl;
    };

    const updateQuestion = async () => {
        if (!editForm) return;

        const confirmModalEl = document.getElementById("confirmUpdateModal");
        if (confirmModalEl) {
            const confirmInstance = window.bootstrap.Modal.getInstance(confirmModalEl);
            confirmInstance?.hide();
        }
        setUpdating(true);

        try {
            const uploadedImageUrl =
                endPoint === "/image-game/" && editForm.imageFile
                    ? await uploadImageFile(editForm.imageFile)
                    : "";

            const payload = {
                set_id: setId,
                question_id: editForm.question_id,
                question: editForm.question,
                explanation: editForm.explanation,
                options: editForm.options,
                answer: editForm.answer,
            };

            if (endPoint === "/image-game/") {
                payload.url = uploadedImageUrl || editForm.url || "";
            }

            console.log("payload", payload);

            const response = await api.patch(`${endPoint}admin/update-question`, payload);
            const updatedImageUrl = getUpdatedImageUrl(
                response.data,
                uploadedImageUrl || editForm.url
            );

            setPopupMessage("Question updated successfully");

            // Update the local state
            setQuestions((prev) =>
                prev.map((category) => ({
                    ...category,
                    questions: (category.questions || []).map((q) =>
                        q.question_id === editForm.question_id
                            ? {
                                ...q,
                                ...editForm,
                                image: updatedImageUrl || q.image,
                                url: updatedImageUrl || q.url
                            }
                            : q
                    ),
                }))
            );

            const modalEl = document.getElementById(`editModal-${editForm.question_id}`);
            if (modalEl) {
                const modalInstance =
                    window.bootstrap.Modal.getInstance(modalEl) ||
                    new window.bootstrap.Modal(modalEl);
                modalInstance.hide();
            }
        } catch (err) {
            console.error(err);
            setPopupMessage("Failed to update question");
        } finally {
            setUpdating(false);
        }
    };

    const approveQuestionSet = async () => {
        try {
            setLoading(true);
            const payload = {
                id: setId,
                action: "approved",
            };
            await api.patch(`${endPoint}admin/set-action`, payload);
            setPopupMessage("Question approved successfully");
            setIsApproved(true);
        } catch (err) {
            console.error(err);
            setPopupMessage("Failed to approve question set");
        }
        setLoading(false);
    };

    const headingLabel = gameInfo.game
        ? `${gameInfo.game.replace(/_/g, " ")}`
        : setId;

    const imageWrapperStyle = {
        width: "100%",
        aspectRatio: "16 / 9",
        background: "linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)",
        borderRadius: "16px",
        marginBottom: "20px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.04)",
    };

    const imageStyle = {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        borderRadius: "16px",
    };

    const getQuestionImageUrl = (question) => {
        if (!question) return "";

        // Check for url field first (for image-game)
        if (question.url) return question.url;
        if (typeof question.image === "string") return question.image;
        if (question.image_url) return question.image_url;
        if (question.imageUrl) return question.imageUrl;
        if (question.photo_url) return question.photo_url;
        if (question.photoUrl) return question.photoUrl;

        return question.image?.url || "";
    };

    const getUpdatedImageUrl = (responseData, fallbackUrl) => {
        const data = responseData?.data ?? responseData;
        return getQuestionImageUrl(data) || fallbackUrl || "";
    };

    const handleImageFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setPopupMessage("Please upload a valid image file");
            e.target.value = "";
            return;
        }

        setEditForm((prev) => {
            if (prev?.imagePreviewUrl) {
                URL.revokeObjectURL(prev.imagePreviewUrl);
            }

            return {
                ...prev,
                imageFile: file,
                imagePreviewUrl: URL.createObjectURL(file),
            };
        });
    };

    return (
        <>
            <Popup message={popupMessage} onClose={() => setPopupMessage("")} />
            <Sidebar />
            <div className="main-wrapper">
                <Header />
                <div className="content-wrapper">
                    <section className="main-section spacer-y">
                        <div className="container">
                            <div className="row gy-4">
                                <div className="col-xl-12">
                                    <div className="common_main_bg_wrapper">
                                        <div className="common_main_heading_wrapper">
                                            <div className="vstack">
                                                <GameBreadcrumb quizId={setId} />
                                                <h1 className="mb-2 mt-3 text-capitalize">{headingLabel}</h1>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="common-table-wrapper mt-4">
                                        <div className="common-table-filter-wrapper">
                                            <div className="common-left-blk">
                                                <div className="common_sub_heading_wrapper mb-0">
                                                    <h2>
                                                        {allQuestions.length} Question
                                                        {allQuestions.length !== 1 ? "s" : ""}
                                                    </h2>
                                                </div>
                                            </div>
                                            <div className="common-right-blk">
                                                <div className="common-sort-blk">
                                                    {!isApproved && allQuestions.length > 0 && (
                                                        <button
                                                            className="secondary_btn py-2 fs-14"
                                                            onClick={() => approveQuestionSet()}
                                                        >
                                                            Approve and Publish
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="common_content_blk">
                                            {loading ? (
                                                <p className="text-center py-5">Loading questions...</p>
                                            ) : allQuestions.length === 0 ? (
                                                <p className="text-center py-5">No questions found.</p>
                                            ) : (
                                                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-xl-2 g-4">
                                                    {allQuestions.map((question, qIndex) => (
                                                        <div
                                                            className="col"
                                                            key={question.question_id}
                                                            id={question.question_id}
                                                        >
                                                            <div className="quiz_card_wrapper">
                                                                <div className="vstack gap-3 h-100 justify-content-between">
                                                                    <div>
                                                                        {/* IMAGE */}
                                                                        {getQuestionImageUrl(question) && (
                                                                            <div style={imageWrapperStyle}>
                                                                                <img
                                                                                    src={getQuestionImageUrl(question)}
                                                                                    alt={`Question ${qIndex + 1}`}
                                                                                    style={imageStyle}
                                                                                    referrerPolicy="no-referrer"
                                                                                    onError={(e) => {
                                                                                        console.warn("✗ Image failed:", getQuestionImageUrl(question));
                                                                                        e.target.style.display = "none";
                                                                                        e.target.parentElement.innerHTML =
                                                                                            '<div style="color:#999;font-size:14px">Image not available</div>';
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        )}

                                                                        {/* Question Type badge */}
                                                                        {question.question_type && (
                                                                            <span className="badge bg-secondary mb-2 text-capitalize">
                                                                                {question.question_type.replace(/_/g, " ")}
                                                                            </span>
                                                                        )}

                                                                        {question.type && (
                                                                            <span className="badge bg-secondary mb-2 text-capitalize">
                                                                                {question.name.replace(/_/g, " ")}
                                                                            </span>
                                                                        )}

                                                                        {/* Question */}
                                                                        <div className="flex-grow-1 mb-4">
                                                                            <h3
                                                                                className={`mb-0 fs-5 ${question.question_id === hash
                                                                                    ? "text-danger fw-bold"
                                                                                    : ""
                                                                                    }`}
                                                                            >
                                                                                {`Q${qIndex + 1}.`} {question.question}
                                                                            </h3>
                                                                        </div>

                                                                        {/* Options */}
                                                                        <ol className="question_list" type="A">
                                                                            {question.options.map((option) => (
                                                                                <li
                                                                                    key={option.label}
                                                                                    className={`question_item ${question.answer === option.label
                                                                                        ? "correct_answer"
                                                                                        : ""
                                                                                        }`}
                                                                                >
                                                                                    {option.value}
                                                                                </li>
                                                                            ))}
                                                                        </ol>

                                                                        {/* Explanation */}
                                                                        <div className="flex-grow-1 mt-4 p-3 bg-light">
                                                                            <div className="mb-0 fs-6 fw-nornal">
                                                                                <strong className="me-2">Explanation:</strong>
                                                                                <MarkdownText>{question.explanation}</MarkdownText>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Edit Button */}
                                                                    <div className="mt-4">
                                                                        <button
                                                                            className="secondary_btn py-2 px-4 w-100 justify-content-center"
                                                                            data-bs-toggle="modal"
                                                                            data-bs-target={`#editModal-${question.question_id}`}
                                                                            onClick={() =>
                                                                                setEditForm({
                                                                                    question_id: question.question_id,
                                                                                    question: question.question,
                                                                                    options: [...question.options], // Create a copy
                                                                                    answer: question.answer,
                                                                                    explanation: question.explanation || "",
                                                                                    url: getQuestionImageUrl(question),
                                                                                    imageFile: null,
                                                                                    imagePreviewUrl: "",
                                                                                })
                                                                            }
                                                                        >
                                                                            Edit Question
                                                                        </button>

                                                                        {/* Edit Modal */}
                                                                        <div
                                                                            className="modal fade"
                                                                            id={`editModal-${question.question_id}`}
                                                                            data-bs-backdrop="static"
                                                                            data-bs-keyboard="false"
                                                                            tabIndex="-1"
                                                                        >
                                                                            <div className="modal-dialog modal-dialog-centered modal-lg">
                                                                                <div className="modal-content">
                                                                                    <div className="modal-header">
                                                                                        <h5 className="modal-title">
                                                                                            Edit Question {qIndex + 1}
                                                                                        </h5>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn-close"
                                                                                            data-bs-dismiss="modal"
                                                                                        />
                                                                                    </div>

                                                                                    <div className="modal-body px-4">
                                                                                        {/* Image URL input field (only for image-game) */}
                                                                                        {endPoint === "/image-game/" && (
                                                                                            <div className="form_field mb-3">
                                                                                                <label className="fs-14 fw-bold">Image URL</label>
                                                                                                <input
                                                                                                    type="text"
                                                                                                    className="form-control"
                                                                                                    placeholder="Enter image URL"
                                                                                                    value={editForm?.url || ""}
                                                                                                    onChange={(e) =>
                                                                                                        setEditForm({
                                                                                                            ...editForm,
                                                                                                            url: e.target.value,
                                                                                                        })
                                                                                                    }
                                                                                                />
                                                                                                <small className="text-muted">Enter a valid image URL (jpg, png, webp, etc.)</small>
                                                                                            </div>
                                                                                        )}

                                                                                        {endPoint === "/image-game/" && (
                                                                                            <div className="form_field mb-3">
                                                                                                <label className="fs-14 fw-bold">Upload Image</label>
                                                                                                <input
                                                                                                    type="file"
                                                                                                    className="form-control"
                                                                                                    accept="image/*"
                                                                                                    onChange={handleImageFileChange}
                                                                                                />
                                                                                                {editForm?.imageFile && (
                                                                                                    <small className="text-muted">
                                                                                                        Selected: {editForm.imageFile.name}
                                                                                                    </small>
                                                                                                )}
                                                                                            </div>
                                                                                        )}

                                                                                        {/* Image preview */}
                                                                                        {(editForm?.imagePreviewUrl || editForm?.url) && (
                                                                                            <div style={imageWrapperStyle}>
                                                                                                <img
                                                                                                    src={editForm.imagePreviewUrl || editForm.url}
                                                                                                    alt="Question preview"
                                                                                                    style={imageStyle}
                                                                                                    referrerPolicy="no-referrer"
                                                                                                    onError={(e) => {
                                                                                                        e.target.style.display = "none";
                                                                                                        e.target.parentElement.innerHTML =
                                                                                                            '<div style="color:#999;font-size:14px">Invalid image URL</div>';
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        )}

                                                                                        {/* Question */}
                                                                                        <div className="form_field">
                                                                                            <label className="fs-14">Question</label>
                                                                                            <textarea
                                                                                                className="form-control"
                                                                                                rows="4"
                                                                                                value={editForm?.question || ""}
                                                                                                onChange={(e) =>
                                                                                                    setEditForm({
                                                                                                        ...editForm,
                                                                                                        question: e.target.value,
                                                                                                    })
                                                                                                }
                                                                                            />
                                                                                        </div>

                                                                                        {/* Options */}
                                                                                        {editForm?.options.map((option, idx) => (
                                                                                            <div
                                                                                                className="form_field"
                                                                                                key={option.label}
                                                                                            >
                                                                                                <label className="fs-14">
                                                                                                    Answer {option.label}.
                                                                                                </label>
                                                                                                <input
                                                                                                    type="text"
                                                                                                    className={`form-control ${editForm.answer === option.label
                                                                                                        ? "correct_answer"
                                                                                                        : ""
                                                                                                        }`}
                                                                                                    value={option.value}
                                                                                                    onChange={(e) => {
                                                                                                        const value = e.target.value;
                                                                                                        setEditForm((prev) => ({
                                                                                                            ...prev,
                                                                                                            options: prev.options.map(
                                                                                                                (opt, i) =>
                                                                                                                    i === idx
                                                                                                                        ? { ...opt, value }
                                                                                                                        : opt
                                                                                                            ),
                                                                                                        }));
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                        ))}

                                                                                        {/* Explanation */}
                                                                                        <div className="form_field">
                                                                                            <label className="fs-14">Explanation</label>
                                                                                            <textarea
                                                                                                className="form-control"
                                                                                                rows="4"
                                                                                                value={editForm?.explanation || ""}
                                                                                                onChange={(e) =>
                                                                                                    setEditForm({
                                                                                                        ...editForm,
                                                                                                        explanation: e.target.value,
                                                                                                    })
                                                                                                }
                                                                                            />
                                                                                        </div>
                                                                                    </div>

                                                                                    <div className="modal-footer border-0 justify-content-center">
                                                                                        <button
                                                                                            className="btn btn-secondary rounded-5 fs-14"
                                                                                            data-bs-dismiss="modal"
                                                                                        >
                                                                                            Cancel
                                                                                        </button>
                                                                                        <button
                                                                                            className="secondary_btn py-2 px-4 fs-14"
                                                                                            onClick={() => {
                                                                                                const modal = new window.bootstrap.Modal(
                                                                                                    document.getElementById(
                                                                                                        "confirmUpdateModal"
                                                                                                    )
                                                                                                );
                                                                                                modal.show();
                                                                                            }}
                                                                                            disabled={updating}
                                                                                        >
                                                                                            {updating ? "Updating..." : "Update"}
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Confirm update modal */}
                        <div
                            className="modal fade"
                            id="confirmUpdateModal"
                            tabIndex="-1"
                            aria-hidden="true"
                        >
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Confirm Update</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            data-bs-dismiss="modal"
                                        ></button>
                                    </div>

                                    <div className="modal-body">
                                        Are you sure you want to update this question?
                                    </div>

                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary rounded-5 fs-14"
                                            data-bs-dismiss="modal"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="secondary_btn py-2 px-4 fs-14"
                                            onClick={updateQuestion}
                                        >
                                            Yes, Update
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default DirectQuestionSetGameQuestions;
